import React from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import { LandingPage } from '@/pages/LandingPage'
import { LoginPage } from '@/pages/auth/LoginPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { OnboardingPage } from '@/pages/onboarding/OnboardingPage'
import { ProfilePage } from '@/pages/profile/ProfilePage'
import { PersonasPage } from '@/pages/personas/PersonasPage'
import { ParentDashboardPage } from '@/pages/parent/ParentDashboardPage'
import { CoursesPage } from '@/pages/courses/CoursesPage'
import { CourseDetailPage } from '@/pages/courses/CourseDetailPage'
import { CourseProgressPage } from '@/pages/courses/CourseProgressPage'
import { SessionPage } from '@/pages/sessions/SessionPage'
import { ChatPage } from '@/pages/chat/ChatPage'
import { ObjectivesPage } from '@/pages/objectives/ObjectivesPage'
import { RewardsPage } from '@/pages/rewards/RewardsPage'
import { GamesPage } from '@/pages/practice/GamesPage'
import { PlayPage } from '@/pages/practice/PlayPage'
import { FriendsPage } from '@/pages/social/FriendsPage'
import { InstitutionPage } from '@/pages/institution/InstitutionPage'
import { SubscriptionPage } from '@/pages/subscription/SubscriptionPage'
import { SubscriptionManagePage } from '@/pages/subscription/SubscriptionManagePage'
import { PrivateRoute } from './PrivateRoute'
import { PublicRoute } from './PublicRoute'
import { AdminRoute } from './AdminRoute'
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage'
import { AppLayout } from '@/components/AppLayout'
import { ChatLayout } from '@/components/ChatLayout'

function PrivateLayout({ children }: { children: React.ReactNode }) {
  return (
    <PrivateRoute>
      <AppLayout>{children}</AppLayout>
    </PrivateRoute>
  )
}

function PrivateChatLayout({ children }: { children: React.ReactNode }) {
  return (
    <PrivateRoute>
      <ChatLayout>{children}</ChatLayout>
    </PrivateRoute>
  )
}

function HomeRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const hasHydrated = useAuthStore((s) => s.hasHydrated)

  if (!hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    )
  }

  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <LandingPage />
}

export const router = createBrowserRouter([
  {
    path: '/auth/login',
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: '/auth/register',
    element: (
      <PublicRoute>
        <RegisterPage />
      </PublicRoute>
    ),
  },
  {
    path: '/auth/verify-email',
    element: <VerifyEmailPage />,
  },
  {
    path: '/auth/forgot-password',
    element: (
      <PublicRoute>
        <ForgotPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <PrivateRoute>
        <OnboardingPage />
      </PrivateRoute>
    ),
  },
  // Chat routes — use ChatLayout (slide-out sidebar + no bottom nav)
  {
    path: '/chat',
    element: <PrivateChatLayout><ChatPage /></PrivateChatLayout>,
  },
  {
    path: '/dashboard',
    element: <PrivateLayout><DashboardPage /></PrivateLayout>,
  },
  {
    path: '/profile',
    element: <PrivateLayout><ProfilePage /></PrivateLayout>,
  },
  {
    path: '/personas',
    element: <PrivateLayout><PersonasPage /></PrivateLayout>,
  },
  {
    path: '/parent/dashboard',
    element: <PrivateLayout><ParentDashboardPage /></PrivateLayout>,
  },
  {
    path: '/courses',
    element: <PrivateLayout><CoursesPage /></PrivateLayout>,
  },
  {
    path: '/courses/:id',
    element: <PrivateLayout><CourseDetailPage /></PrivateLayout>,
  },
  {
    path: '/courses/:id/progress',
    element: <PrivateLayout><CourseProgressPage /></PrivateLayout>,
  },
  {
    path: '/courses/:courseId/sessions/:sessionId',
    element: <PrivateChatLayout><SessionPage /></PrivateChatLayout>,
  },
  {
    path: '/jeux',
    element: <PrivateLayout><GamesPage /></PrivateLayout>,
  },
  {
    path: '/jeux/:gameId',
    element: <PrivateLayout><PlayPage /></PrivateLayout>,
  },
  {
    path: '/rewards',
    element: <PrivateLayout><RewardsPage /></PrivateLayout>,
  },
  {
    path: '/friends',
    element: <PrivateLayout><FriendsPage /></PrivateLayout>,
  },
  {
    path: '/institution',
    element: <PrivateLayout><InstitutionPage /></PrivateLayout>,
  },
  {
    path: '/objectives',
    element: <PrivateLayout><ObjectivesPage /></PrivateLayout>,
  },
  {
    path: '/subscription',
    element: <PrivateLayout><SubscriptionPage /></PrivateLayout>,
  },
  {
    path: '/subscription/manage',
    element: <PrivateLayout><SubscriptionManagePage /></PrivateLayout>,
  },
  {
    path: '/admin',
    element: (
      <AdminRoute>
        <AppLayout><AdminUsersPage /></AppLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/',
    element: <HomeRoute />,
  },
])

